import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import dateFormat from "dateformat";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { editCreditNote } from "../../../models/CreditNoteModel";
import { getInvoices } from "../../../models/InvoiceModel";
import { apiError } from "../../../helpers/ErrorHelper";
import Select from "react-select";
import { useTheme } from "../../../context/ThemeContext";

const UpdateCreditNoteModal = ({
  showModal,
  setShowModal,
  modalKey,
  creditNoteData,
  setCreditNoteData,
}) => {
  const { state } = useAuth();
  const { selectBoxTheme } = useTheme();
  const navigate = useNavigate();
  const loader = document.getElementById("loading");
  const user_data = state.user_data;

  const [loading, setLoading] = useState(false);
  const [invoice_link, setInvoiceLink] = useState("");
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [credit_note_bill_date, setCreditNoteBillDate] = useState("");
  const [credit_note_month, setCreditNoteMonth] = useState("");
  const [credit_note_po_number, setCreditNotePONumber] = useState("");
  const [errors, setErrors] = useState({
    credit_note_bill_date: "",
    credit_note_month: "",
    credit_note_po_number: "",
  });

  const parseCreditNoteMonth = (value) => {
    if (!value) return "";
    if (value instanceof Date) return value;
    const parts = String(value).split("-");
    if (parts.length === 2) {
      const [mm, yyyy] = parts;
      return new Date(`${yyyy}-${mm}-01`);
    }
    return new Date(value);
  };

  useEffect(() => {
    const loadInvoices = async () => {
      const clientId = creditNoteData?._client_id;
      if (!clientId || !showModal) {
        setInvoiceOptions([]);
        return;
      }
      setInvoicesLoading(true);
      try {
        const response = await getInvoices({
          user_token: user_data.user_token,
          page: 1,
          limit: 500,
          _client_id: clientId,
          exclude_with_credit_note: 1,
        });
        const rows = response?.invoices || [];
        const options = rows.map((inv) => ({
          value: inv.invoice_unique_id,
          label: `${inv.invoice_number} (${dateFormat(inv.invoice_bill_date, "dd mmm yyyy")})`,
        }));
        if (
          creditNoteData.ref_invoice_unique_id &&
          !options.some((o) => o.value === creditNoteData.ref_invoice_unique_id)
        ) {
          options.unshift({
            value: creditNoteData.ref_invoice_unique_id,
            label:
              creditNoteData.ref_invoice_number ||
              creditNoteData.ref_invoice_unique_id,
          });
        }
        setInvoiceOptions(options);
      } catch (error) {
        setInvoiceOptions([]);
      } finally {
        setInvoicesLoading(false);
      }
    };

    if (creditNoteData) {
      setCreditNoteBillDate(
        creditNoteData.credit_note_bill_date
          ? new Date(creditNoteData.credit_note_bill_date)
          : ""
      );
      setCreditNoteMonth(parseCreditNoteMonth(creditNoteData.credit_note_month));
      setCreditNotePONumber(creditNoteData.credit_note_po_number || "");
      if (creditNoteData.ref_invoice_unique_id) {
        setInvoiceLink({
          value: creditNoteData.ref_invoice_unique_id,
          label: creditNoteData.ref_invoice_number || creditNoteData.ref_invoice_unique_id,
        });
      } else {
        setInvoiceLink("");
      }
      setErrors({});
      loadInvoices();
    }
  }, [creditNoteData, modalKey, showModal, user_data.user_token]);

  const closeModal = () => {
    setCreditNoteBillDate("");
    setCreditNoteMonth("");
    setCreditNotePONumber("");
    setInvoiceLink("");
    setErrors({});
    setShowModal(false);
  };

  const updateCreditNote = async () => {
    loader.style.display = "block";
    setLoading(true);

    try {
      setErrors({});
      const formData = {
        credit_note_bill_date: credit_note_bill_date
          ? dateFormat(credit_note_bill_date, "dd-mm-yyyy")
          : "",
        credit_note_month: credit_note_month
          ? dateFormat(credit_note_month, "mmm-yyyy")
          : "",
        credit_note_po_number: credit_note_po_number,
        invoice_unique_id: invoice_link?.value ? invoice_link.value : "",
      };

      const response = await editCreditNote(
        user_data.user_token,
        creditNoteData.credit_note_unique_id,
        formData
      );

      if (response.status) {
        toast.success(response.message || "Credit note updated.");
        if (response.data?.credit_note_data) {
          setCreditNoteData(response.data.credit_note_data);
        }
        closeModal();
      } else {
        setErrors(response.error || {});
        toast.error(response.message || "Failed to update credit note");
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token === 1) {
        navigate("/re-login");
      }
    }

    loader.style.display = "none";
    setLoading(false);
  };

  return (
    <Modal key={modalKey} show={showModal} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update credit note details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row gy-2">
          <div className="col-xl-6">
            <label className="form-label">
              Credit note date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              selected={credit_note_bill_date}
              onChange={setCreditNoteBillDate}
              dateFormat="dd-MM-yyyy"
            />
            {errors.credit_note_bill_date && (
              <p className="text-danger">{errors.credit_note_bill_date}</p>
            )}
          </div>
          <div className="col-xl-6">
            <label className="form-label">Credit note month</label>
            <DatePicker
              className="form-control"
              placeholderText="MMM-YYYY"
              selected={credit_note_month}
              onChange={setCreditNoteMonth}
              showMonthYearPicker
              dateFormat="MMM-yyyy"
            />
            {errors.credit_note_month && (
              <p className="text-danger">{errors.credit_note_month}</p>
            )}
          </div>
          <div className="col-xl-12">
            <label className="form-label">Link to invoice (optional)</label>
            <Select
              className="form-control custom-select"
              value={invoice_link}
              onChange={setInvoiceLink}
              options={invoiceOptions}
              placeholder={invoicesLoading ? "Loading invoices…" : "— No linked invoice —"}
              isSearchable
              isClearable
              isLoading={invoicesLoading}
              styles={selectBoxTheme}
            />
          </div>
          <div className="col-xl-6">
            <label className="form-label">PO number</label>
            <input
              type="text"
              className="form-control"
              placeholder="PO Number"
              value={credit_note_po_number}
              onChange={(e) => setCreditNotePONumber(e.target.value)}
            />
            {errors.credit_note_po_number && (
              <p className="text-danger">{errors.credit_note_po_number}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={closeModal}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={updateCreditNote}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm align-middle"></span>
              Loading...
            </>
          ) : (
            "Save changes"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateCreditNoteModal;
